/**
 * Work Order Detail Page
 *
 * Shows full details of a work order including:
 * - Customer info and address
 * - Wasserij items
 * - Photos
 * - Actions (start inspection, complete pickup/delivery)
 */

import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, Button, Badge, WorkOrderStatusBadge } from '../components/ui';
import type { WerkOrder, WasserijItem } from '../lib/salesforce';

// Mock data for development
const MOCK_WORK_ORDER: WerkOrder = {
  Id: 'wo-1',
  Name: 'WO-12345',
  Werk_Order_Status__c: 'Ophalen ingepland',
  Klantnaam__c: 'Familie De Vries',
  Naam_op_Factuur__c: 'J.P. de Vries',
  Ophaaladres_formule__c: 'Hoofdstraat 123\n1234 AB Amsterdam',
  Afleveradres_formule__c: 'Hoofdstraat 123\n1234 AB Amsterdam',
  Opmerkingen_Ophalen__c: 'Bel aan bij buren als niet thuis (nr. 125)',
  Ophaal_moment_definitief__c: '09:00 - 11:00',
  Totaal_m2_vloerkleed__c: 12.5,
};

const MOCK_ITEMS: WasserijItem[] = [
  {
    Id: 'wi-1',
    Name: 'WI-001',
    Werk_Order__c: 'wo-1',
    Product__r: { Name: 'Perzisch tapijt', ProductCode: 'WAS-PRIM-PERZISCH' },
    Lengte__c: 300,
    Breedte__c: 200,
    Oppervlakte_m__c: 6.0,
    Voorinspectie_gedaan__c: false,
  },
  {
    Id: 'wi-2',
    Name: 'WI-002',
    Werk_Order__c: 'wo-1',
    Product__r: { Name: 'Wollen vloerkleed', ProductCode: 'WAS-PRIM-WOL' },
    Lengte__c: 250,
    Breedte__c: 150,
    Oppervlakte_m__c: 3.75,
    Voorinspectie_gedaan__c: false,
  },
  {
    Id: 'wi-3',
    Name: 'WI-003',
    Werk_Order__c: 'wo-1',
    Product__r: { Name: 'Machinaal tapijt', ProductCode: 'WAS-PRIM-MACHINAAL' },
    Lengte__c: 180,
    Breedte__c: 120,
    Oppervlakte_m__c: 2.16,
    Voorinspectie_gedaan__c: true,
  },
];

export function WorkOrderPage() {
  const { workOrderId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // In real app, fetch from store/API
  const workOrder = MOCK_WORK_ORDER;
  const items = MOCK_ITEMS;

  const isPickupPhase = [
    'Ophalen plannen',
    'Ophaalvoorstel gedaan',
    'Ophaalvoorstel geen reactie',
    'Ophalen ingepland',
  ].includes(workOrder.Werk_Order_Status__c);

  const handleStartNavigation = () => {
    const address = isPickupPhase
      ? workOrder.Ophaaladres_formule__c
      : workOrder.Afleveradres_formule__c;

    if (address) {
      // Open in maps app
      const encodedAddress = encodeURIComponent(address.replace(/\n/g, ', '));
      window.open(`https://maps.google.com/?q=${encodedAddress}`, '_blank');
    }
  };

  const handleCall = () => {
    // In real app, get phone number from account/contact
    window.location.href = 'tel:+31612345678';
  };

  const handleCompleteStop = async () => {
    setIsLoading(true);
    // In real app, call API to complete the stop
    setTimeout(() => {
      setIsLoading(false);
      navigate('/routes');
    }, 1000);
  };

  return (
    <div className="space-y-lg">
      {/* Back button */}
      <Link
        to="/routes"
        className="inline-flex items-center gap-xs text-kleda-primary hover:underline"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Terug naar routes
      </Link>

      {/* Header card */}
      <Card>
        <CardContent>
          <div className="flex items-start justify-between mb-md">
            <div>
              <p className="text-sm text-kleda-text-muted mb-xs">{workOrder.Name}</p>
              <h2 className="font-heading text-xl font-bold text-kleda-text-main">
                {workOrder.Naam_op_Factuur__c || workOrder.Klantnaam__c}
              </h2>
            </div>
            <WorkOrderStatusBadge status={workOrder.Werk_Order_Status__c} />
          </div>

          {/* Time slot */}
          <div className="flex items-center gap-sm mb-md text-kleda-text-muted">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              {isPickupPhase
                ? workOrder.Ophaal_moment_definitief__c
                : workOrder.Aflever_moment_definitief__c || '-'}
            </span>
          </div>

          {/* Address */}
          <div className="bg-kleda-bg-clean rounded-kleda-sm p-md mb-md">
            <p className="text-sm font-medium text-kleda-text-muted mb-xs">
              {isPickupPhase ? 'Ophaaladres' : 'Afleveradres'}
            </p>
            <p className="text-kleda-text-main whitespace-pre-line">
              {isPickupPhase
                ? workOrder.Ophaaladres_formule__c
                : workOrder.Afleveradres_formule__c}
            </p>
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-2 gap-sm">
            <Button variant="outline" onClick={handleStartNavigation}>
              <svg className="w-4 h-4 mr-xs" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Navigeer
            </Button>
            <Button variant="outline" onClick={handleCall}>
              <svg className="w-4 h-4 mr-xs" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Bellen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {workOrder.Opmerkingen_Ophalen__c && isPickupPhase && (
        <Card>
          <CardHeader>
            <CardTitle>Opmerkingen</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-kleda-text-main whitespace-pre-line">
              {workOrder.Opmerkingen_Ophalen__c}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Items ({items.length})</CardTitle>
            <span className="text-sm text-kleda-text-muted">
              {workOrder.Totaal_m2_vloerkleed__c} m² totaal
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-sm">
            {items.map((item) => (
              <ItemCard key={item.Id} item={item} workOrderId={workOrderId!} />
            ))}
          </div>
        </CardContent>
        {isPickupPhase && (
          <CardFooter>
            <Link to={`/workorder/${workOrderId}/inspection`} className="w-full">
              <Button fullWidth variant="outline">
                <svg className="w-4 h-4 mr-xs" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Start voorinspectie
              </Button>
            </Link>
          </CardFooter>
        )}
      </Card>

      {/* Complete action */}
      <Card>
        <CardContent>
          <Button
            fullWidth
            size="lg"
            onClick={handleCompleteStop}
            isLoading={isLoading}
          >
            {isPickupPhase ? 'Ophalen afronden' : 'Afleveren afronden'}
          </Button>
          <p className="text-center text-sm text-kleda-text-muted mt-sm">
            Markeer als {isPickupPhase ? 'opgehaald' : 'afgeleverd'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

interface ItemCardProps {
  item: WasserijItem;
  workOrderId: string;
}

function ItemCard({ item, workOrderId }: ItemCardProps) {
  return (
    <Link
      to={`/workorder/${workOrderId}/inspection/${item.Id}`}
      className="flex items-center gap-md p-md bg-kleda-bg-clean rounded-kleda-sm hover:bg-kleda-primary/5 transition-colors"
    >
      {/* Icon/Preview */}
      <div className="w-12 h-12 bg-white rounded-kleda-sm flex items-center justify-center shadow-kleda-sm flex-shrink-0">
        <svg className="w-6 h-6 text-kleda-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-sm">
          <p className="font-medium text-kleda-text-main truncate">
            {item.Product__r?.Name || 'Product'}
          </p>
          {item.Voorinspectie_gedaan__c && (
            <Badge variant="success" size="sm">Geïnspecteerd</Badge>
          )}
        </div>
        <p className="text-sm text-kleda-text-muted">
          {item.Lengte__c && item.Breedte__c
            ? `${item.Lengte__c} × ${item.Breedte__c} cm`
            : item.Diameter__c
            ? `Ø ${item.Diameter__c} cm`
            : 'Afmetingen onbekend'}
          {item.Oppervlakte_m__c && ` • ${item.Oppervlakte_m__c} m²`}
        </p>
      </div>

      {/* Arrow */}
      <svg className="w-5 h-5 text-kleda-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}
